/**
 * Normalise transaction descriptions for matching.
 *
 * The parsers already do heavy lifting — extractedName, paymentMethod, upiId
 * are available on ParsedTransaction. This module handles the remaining cases:
 * - Comparing extractedName (from parsers) to merchantName (from DB)
 * - Fallback normalisation when extractedName is null
 *
 * Uses fuzzball (Python fuzzywuzzy port) for robust string similarity.
 * Environment-agnostic: runs in WebWorker or server.
 */

import * as fuzz from "fuzzball"
import { aliasesMatch } from "./aliases"

/** Corporate suffixes to strip from names */
const CORPORATE_SUFFIXES = /\b(PRIVATE|PVT\.?|LIMITED|LTD\.?)\s*\w*/gi

/**
 * token_set_ratio threshold for merchant matching.
 *
 * From real data testing:
 * - True positives (Swiggy ↔ SWIGGY IT PVT LTD) score 100
 * - True positives (ZEPTO ↔ PayU*ZEPTO MARKETPLACE Bangalore) score 100
 * - False positives (MUMBAI PAV CO ↔ AMAZON Mumbai) score 63
 * - False positives (BANGALORE ELECTRICIT ↔ KUSHALS RETAIL PVT LTD BANGALORE) score 62
 *
 * 80 cleanly separates the two groups.
 */
const TOKEN_SET_THRESHOLD = 80

/**
 * Normalise a merchant name for comparison.
 *
 * Strips corporate suffixes, collapses whitespace, lowercases.
 * Works on both extractedName (from parsers) and merchantName (from DB).
 */
export function normaliseName(name: string): string {
  return name
    .replace(CORPORATE_SUFFIXES, "")
    .replace(/[^\w\s]/g, " ") // punctuation → space
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
}

/**
 * Get the best available name for matching from a parsed transaction.
 * Prefers extractedName (parser output), falls back to description.
 */
export function getMatchName(extractedName: string | null, description: string): string {
  if (extractedName && extractedName.trim().length > 0) {
    return normaliseName(extractedName)
  }
  return normaliseName(description)
}

/**
 * Check if two names likely refer to the same merchant.
 *
 * Uses fuzzball's token_set_ratio — handles word reordering, extra words,
 * and partial matches robustly. token_set_ratio works by:
 * 1. Tokenising both strings
 * 2. Finding the intersection (common tokens)
 * 3. Comparing: intersection alone vs intersection + remainder of each
 * 4. Returning the best score
 *
 * This makes "Swiggy" vs "SWIGGY IT PRIVATE LIMITED" score 100 (Swiggy is
 * fully contained) while "MUMBAI PAV CO" vs "AMAZON Mumbai" scores 63.
 */
export function isSameMerchant(a: string, b: string): boolean {
  const normA = normaliseName(a)
  const normB = normaliseName(b)

  if (normA.length === 0 || normB.length === 0) return false
  if (normA === normB) return true

  // Alias table — catches "BUNDL TECHNOLOGIES" ↔ "Swiggy", "ANI TECHNOLOGIES" ↔ "Ola", etc.
  // Always runs regardless of string length.
  if (aliasesMatch(normA, normB)) return true

  // Guard: skip fuzzy matching for very short or very mismatched lengths.
  // SBI truncates names to ~8 chars, creating "MUMBAI L" which fuzzy-matches
  // "AMAZON Mumbai", "CLEARTRIP MUMBAI", etc. Require:
  // - Both names at least 4 chars
  // - Shorter name at least 40% of longer (blocks "MUMBAI L" vs "DECATHLON SPORTS INDIA BANGALORE")
  const shorter = Math.min(normA.length, normB.length)
  const longer = Math.max(normA.length, normB.length)
  if (shorter < 4 || shorter / longer < 0.4) return false

  // token_set_ratio handles reordering + extra words: "Swiggy" ↔ "SWIGGY IT PVT LTD" = 100
  if (fuzz.token_set_ratio(normA, normB) >= TOKEN_SET_THRESHOLD) return true

  // plain ratio catches near-identical strings with spacing diffs: "UBEREATS" ↔ "UBER EATS" = 94
  if (fuzz.ratio(normA, normB) >= 90) return true

  return false
}
